import React, { useMemo, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useInfiniteScroll } from "./hooks/useInfiniteScroll";
import {
  InfiniteScrollLoading,
  ErrorState,
  EmptyState,
  SkeletonLoader,
} from "./Loading";
import {
  InfiniteScrollProps,
  InfiniteScrollState,
} from "./types/infiniteScroll";

export const InfiniteScroll = <T, P = any, S = any>({
  // Required props
  apiAction,
  itemsData,
  children,

  params,
  initialPageSize = 40,
  startPage = 1,
  scroll = { threshold: 200, debounceMs: 150 },
  loading = {},
  search,
  apiParams,
  debug = false,
  autoLoadFirst = true,
  maxPages = 0,
  retry = {
    enabled: true,
    maxAttempts: 3,
    delay: 2000,
  },
  useDirectResponse = false,

  className = "",
  style,
  renderError,
  renderEmpty,
  renderLoading,
}: InfiniteScrollProps<T, P, S>) => {
  const config = useMemo(
    () => ({
      apiAction,
      itemsData,
      params,
      initialPageSize,
      startPage,
      scroll,
      loading,
      search,
      apiParams,
      debug,
      autoLoadFirst,
      maxPages,
      retry,
      useDirectResponse,
    }),
    [
      apiAction,
      itemsData,
      params,
      initialPageSize,
      startPage,
      scroll,
      loading,
      search,
      apiParams,
      debug,
      autoLoadFirst,
      maxPages,
      retry,
      useDirectResponse,
    ]
  );

  const {
    items,
    state,
    loadMore,
    reset,
    refresh,
    retry: retryFn,
    isLoading,
    hasMore,
    error,
    scrollDetection,
  } = useInfiniteScroll<T, P, S>(config);

  // Sentinel ref for intersection observer
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Attach sentinel to scroll detection when items change or component mounts
  useEffect(() => {
    console.log(
      "[InfiniteScroll] Attaching sentinel - hasMore:",
      hasMore,
      "isLoading:",
      isLoading,
      "sentinelRef:",
      !!sentinelRef.current
    );

    if (
      scrollDetection?.attachSentinel &&
      sentinelRef.current &&
      hasMore &&
      !isLoading
    ) {
      console.log(
        "[InfiniteScroll] Calling attachSentinel with:",
        sentinelRef.current
      );
      scrollDetection.attachSentinel(sentinelRef.current);
    } else {
      console.log(
        "[InfiniteScroll] Not attaching sentinel - conditions not met"
      );
    }
  }, [items.length, scrollDetection?.attachSentinel, hasMore, isLoading]);

  const renderLoadingState = (isInitial: boolean = false) => {
    if (renderLoading) {
      return renderLoading(isInitial);
    }

    return (
      <InfiniteScrollLoading
        config={loading}
        isInitial={isInitial}
        className="my-4"
      />
    );
  };

  const renderErrorState = () => {
    if (renderError) {
      return renderError(error || "An error occurred", retryFn);
    }

    return (
      <ErrorState
        error={error || "An error occurred while loading data"}
        onRetry={retryFn}
        className="my-4"
      />
    );
  };

  const renderEmptyState = () => {
    if (renderEmpty) {
      return renderEmpty();
    }

    return (
      <EmptyState
        message="Δεν βρέθηκαν αποτελέσματα για την αναζήτησή σας"
        className="my-8"
      />
    );
  };

  const showInitialLoading = isLoading && items.length === 0 && !error;

  const showMoreLoading =
    isLoading && items.length > 0 && !error && state.loading === "loading";

  const showEmpty =
    !isLoading &&
    items.length === 0 &&
    !error &&
    (state.loading === "complete" || state.loading === "idle");

  const showError = error && state.loading === "error";

  return (
    <div
      className={`infinite-scroll-container ${className}`}
      style={style}
      role="feed"
      aria-busy={isLoading}
      aria-live="polite"
    >
      {debug && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-opacity-75 fixed top-4 right-4 z-50 max-w-xs rounded-lg bg-black p-3 text-xs text-white"
        >
          <div className="mb-2 font-bold">Infinite Scroll Debug</div>
          <div>Page: {state.currentPage}</div>
          <div>Items: {items.length}</div>
          <div>Loading: {state.loading}</div>
          <div>Has More: {String(hasMore)}</div>
          <div>Error: {error || "None"}</div>
          <div>Search Hash: {state.searchHash?.slice(0, 20)}...</div>
          <div>Is Loading: {String(isLoading)}</div>
        </motion.div>
      )}

      {showInitialLoading && (
        <motion.div key="initial-loading">
          {renderLoadingState(true)}
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {showError && (
          <motion.div
            key="error"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            {renderErrorState()}
          </motion.div>
        )}
      </AnimatePresence>

      {showEmpty && <div key="empty">{renderEmptyState()}</div>}

      <AnimatePresence>
        {items.length > 0 && !showError && (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children(items, state)}

            {/* Sentinel element - invisible trigger for loading more */}
            {hasMore && !isLoading && (
              <div
                ref={sentinelRef}
                className="flex h-4 w-full items-center justify-center"
                style={{
                  position: "relative",
                  backgroundColor: debug
                    ? "rgba(255, 0, 0, 0.1)"
                    : "transparent",
                  border: debug ? "1px dashed red" : "none",
                  pointerEvents: "none",
                }}
                aria-hidden="true"
                data-testid="infinite-scroll-sentinel"
              >
                {debug && (
                  <span className="font-mono text-xs text-red-500">
                    SENTINEL - Scroll to trigger next page
                  </span>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {showMoreLoading && (
        <div key="more-loading">{renderLoadingState(false)}</div>
      )}

      {/* <AnimatePresence>
        {!hasMore && items.length > 0 && !isLoading && !error && state.loading === 'complete' && (
          <motion.div
            key="end-indicator"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="flex items-center justify-center py-6"
          >
            <div className="flex items-center text-sm text-gray-500">
              <div className="mr-4 flex-1 border-t border-gray-300" />
              <span className="px-3 font-medium">You've reached the end</span>
              <div className="ml-4 flex-1 border-t border-gray-300" />
            </div>
          </motion.div>
        )}
      </AnimatePresence> */}
    </div>
  );
};
